import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, Play, Pause, RotateCcw, Timer, ChefHat } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function CookingMode({ recipe, onClose }) {
  const steps = recipe?.steps || [];
  const [i, setI] = useState(0);
  const [secs, setSecs] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setSecs((s) => {
        if (s <= 1) {
          clearInterval(ref.current); setRunning(false);
          toast.success("⏱️ Timer terminato!");
          try { new AudioContext(); } catch {}
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const addMin = (m) => { setSecs((s) => s + m * 60); };
  const reset = () => { setRunning(false); setSecs(0); };

  const stepMinutes = (() => {
    const m = String(steps[i] || "").match(/(\d+)(?:\s*[-–]\s*\d+)?\s*min(?:uti)?\b/i);
    return m ? parseInt(m[1], 10) : null;
  })();
  const startStepTimer = () => { if (stepMinutes) { setSecs(stepMinutes * 60); setRunning(true); } };

  // Keep the screen awake while cooking
  useEffect(() => {
    let lock = null;
    const request = async () => {
      try { if ("wakeLock" in navigator) lock = await navigator.wakeLock.request("screen"); } catch {}
    };
    request();
    const onVis = () => { if (document.visibilityState === "visible") request(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (lock) { try { lock.release(); } catch {} }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((v) => Math.min(steps.length - 1, v + 1));
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps.length, onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col pointer-events-auto" data-testid="cooking-mode">
      <header className="flex items-center justify-between px-5 h-16 border-b shrink-0">
        <div className="flex items-center gap-2 font-heading font-semibold truncate">
          <ChefHat className="h-5 w-5 text-primary shrink-0" /> <span className="truncate">{recipe?.title || "Modalità cucina"}</span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="cooking-close"><X className="h-5 w-5" /></Button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-6 overflow-y-auto py-8">
        <div className="text-sm font-medium text-muted-foreground" data-testid="cooking-step-count">Passo {i + 1} di {steps.length || 1}</div>
        <p className="max-w-2xl text-2xl sm:text-3xl leading-relaxed font-medium" data-testid="cooking-step-text">
          {steps[i] || "Nessun passo disponibile."}
        </p>
        {stepMinutes && (
          <Button variant="secondary" onClick={startStepTimer} data-testid="cooking-step-timer">
            <Timer className="h-4 w-4 mr-2" /> Avvia timer {stepMinutes} min per questo passo
          </Button>
        )}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="lg" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0} data-testid="cooking-prev">
            <ChevronLeft className="h-5 w-5 mr-1" /> Indietro
          </Button>
          <Button size="lg" onClick={() => setI((v) => Math.min(steps.length - 1, v + 1))} disabled={i >= steps.length - 1} data-testid="cooking-next">
            Avanti <ChevronRight className="h-5 w-5 ml-1" />
          </Button>
        </div>
      </main>

      <footer className="border-t shrink-0 px-5 py-4">
        <div className="max-w-md mx-auto flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 text-4xl font-mono-data font-bold tabular-nums" data-testid="cooking-timer">
            <Timer className="h-7 w-7 text-accent" /> {fmt(secs)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[1, 3, 5, 10].map((m) => (
              <Button key={m} variant="secondary" size="sm" onClick={() => addMin(m)} data-testid={`cooking-add-${m}`}>+{m} min</Button>
            ))}
            <Button size="sm" onClick={() => secs > 0 && setRunning((r) => !r)} disabled={secs === 0} data-testid="cooking-timer-toggle">
              {running ? <><Pause className="h-4 w-4 mr-1" /> Pausa</> : <><Play className="h-4 w-4 mr-1" /> Avvia</>}
            </Button>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="cooking-timer-reset"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
